// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { OperatorConfiguration } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Configuration/Operator';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { QueryTable } from '../../../components/Table/QueryTable';
import { useParties } from '../../../contexts/PartiesContext';
import useArchiveOperatorConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveOperatorConfiguration';
import useCreateServiceUpgradeConsent from '../../../hooks/mutations/registry/configuration/useCreateServiceUpgradeConsent';
import useMutate from '../../../hooks/mutations/useMutate';
import { useCsvExport } from '../../../hooks/other/useCsvExport';
import useRegistryOperatorConfiguration from '../../../hooks/queries/registry/configuration/useRegistryOperatorConfiguration';
import useRegistryOperatorConfigurations from '../../../hooks/queries/registry/configuration/useRegistryOperatorConfigurations';
import useServiceUpgradeConsents from '../../../hooks/queries/registry/onboarding/useServiceUpgradeConsents';
import TemplateContract from '../../../utils/TemplateContract';
import {
  SUC_ARC_OP_CONF,
  ERR_ARC_OP_CONF,
  SUC_GRA_UPG_CONS,
  ERR_GRA_UPG_CONS,
} from '../../../utils/strings';
import { CreateOperatorConfigurationDialog } from './CreateOperatorConfigurationDialog';

type Row = TemplateContract<OperatorConfiguration>;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      valueGetter: (params) => params.row.payload.operator,
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = ({
  handleExport,
  isExportDisabled,
  isCreateDisabled,
  isLoading,
  onCreateClick,
  showGrantUpgradeConsent,
  onGrantUpgradeConsent,
}: {
  handleExport: () => void;
  isExportDisabled: boolean;
  isCreateDisabled: boolean;
  isLoading: boolean;
  onCreateClick: () => void;
  showGrantUpgradeConsent: boolean;
  onGrantUpgradeConsent: () => Promise<unknown>;
}) => {
  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          <SyncButton onClick={onCreateClick} disabled={isCreateDisabled}>
            Create operator configuration
          </SyncButton>
          {showGrantUpgradeConsent && (
            <AsyncButton onClick={onGrantUpgradeConsent}>Grant upgrade consent</AsyncButton>
          )}
          <SyncButton onClick={handleExport} disabled={isExportDisabled}>
            Export CSV
          </SyncButton>
        </>
      )}
    </GridToolbarContainer>
  );
};

export const OperatorConfigurations = () => {
  const [createOperatorConfigurationIsOpen, setCreateOperatorConfigurationIsOpen] = useState(false);

  const operatorConfigurations = useRegistryOperatorConfigurations();
  const archiveOperatorConfiguration = useArchiveOperatorConfiguration();
  const operatorConfiguration = useRegistryOperatorConfiguration();
  const { operator, party } = useParties();
  const mutate = useMutate();

  const rows = operatorConfigurations.data ?? [];

  const archive = useCallback(
    (row: Row) => {
      const payload = {
        operatorConfigurationCid: row.contractId,
      };
      return mutate(archiveOperatorConfiguration, payload, SUC_ARC_OP_CONF, ERR_ARC_OP_CONF);
    },
    [archiveOperatorConfiguration, mutate],
  );

  const isOperator = operator === party;

  const serviceUpgradeConsents = useServiceUpgradeConsents();
  const createServiceUpgradeConsent = useCreateServiceUpgradeConsent();

  // This is the only UI path for creating the operator's ServiceUpgradeConsent: it
  // cannot be bundled into the operator configuration create, since Canton's
  // interactive submission service seems to accept only one command per prepare request.
  // Visible to the operator only while a configuration exists without an
  // operator-authorized consent, and self-hides once granted. The upgrader is taken
  // from the existing configuration so the consent always names the same party.
  const hasOperatorConsent = (serviceUpgradeConsents.data ?? []).some(
    (c) => c.payload.authorizer === operator,
  );
  const showGrantUpgradeConsent =
    isOperator &&
    !!operatorConfiguration.data &&
    serviceUpgradeConsents.isSuccess &&
    !hasOperatorConsent;

  const grantUpgradeConsent = useCallback(() => {
    const upgrader = operatorConfiguration.data?.payload.upgrader;
    if (!upgrader) return Promise.resolve();
    return mutate(createServiceUpgradeConsent, { upgrader }, SUC_GRA_UPG_CONS, ERR_GRA_UPG_CONS);
  }, [createServiceUpgradeConsent, mutate, operatorConfiguration.data]);

  const createActions = useCallback(() => {
    const actions: Action[] = [];
    if (isOperator) actions.push({ name: 'Archive', action: archive });
    return actions;
  }, [isOperator, archive]);

  const columns = useMemo(() => createHeaders(createActions), [createActions]);

  const { handleExport, isExportDisabled } = useCsvExport(columns, rows, {
    filename: 'operator_configurations',
  });

  const isLoading = operatorConfigurations.isLoading || archiveOperatorConfiguration.isLoading;
  if (isLoading) return <Loading />;

  const hasData = operatorConfigurations.data;
  if (!hasData) return <Typography>No configurations</Typography>;

  return (
    <>
      {createOperatorConfigurationIsOpen && (
        <CreateOperatorConfigurationDialog
          open={createOperatorConfigurationIsOpen}
          onClose={() => setCreateOperatorConfigurationIsOpen(false)}
        />
      )}
      <QueryTable
        title="Operator Configurations"
        variant="h3"
        enableTableWrapper
        columns={columns}
        rowQuery={operatorConfigurations}
        rowKey={(a) => a.contractId}
        toolbar={() => (
          <Toolbar
            handleExport={handleExport}
            isExportDisabled={isExportDisabled}
            isCreateDisabled={!!operatorConfiguration.data}
            isLoading={operatorConfiguration.isLoading}
            onCreateClick={() => setCreateOperatorConfigurationIsOpen(true)}
            showGrantUpgradeConsent={showGrantUpgradeConsent}
            onGrantUpgradeConsent={grantUpgradeConsent}
          />
        )}
      />
    </>
  );
};
